import { Form, FormControl, FormField } from "@/components/ui/form/form";
import PageTransition from "@/components/ui/PageTransition";
import PhoneInputField from "@/components/ui/phone-input/phone-input";
import { removeDialCode } from "@/components/ui/phone-input/phone-input.utils";
import { Progress } from "@/components/ui/progress";
import SubmitButton from "@/components/ui/submit-button";
import { getHelpCenterUrl } from "@/lib/utils";
import { COUNTRIES } from "@/constants/countries";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationStatus } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PHONE_NUMBER_STATEMENT } from "@/constants/common-constants";
import { CheckCircle } from "lucide-react";

const formSchema = z.object({
	phone: z
		.string({
			message: "Please enter a phone number",
		})
		.min(10, "Please enter a phone number")
		.max(15, "Phone number must not exceed 15 digits"),
	countryCode: z
		.number({
			message: "Please enter a phone number",
		})
		.min(1, "Please select a valid country"),
});

type FormSchema = z.infer<typeof formSchema>;

export interface VerifyPhoneProps {
	submitMutationStatus: MutationStatus;
	onSubmit: (phone: string, countryCode: number) => void;
}

const VerifyPhone = ({ submitMutationStatus, onSubmit }: VerifyPhoneProps) => {
	const [progress, setProgress] = useState(15);
	const phoneInputRef = useRef<HTMLInputElement>(null);

	const form = useForm<FormSchema>({
		resolver: zodResolver(formSchema),
		mode: "onBlur",
		defaultValues: {
			countryCode: undefined,
			phone: "",
		},
	});

	useEffect(() => {
		const timer = setTimeout(() => setProgress(30), 20);
		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		// Focus the input after a small delay to ensure the component is mounted
		const focusTimer = setTimeout(() => {
			phoneInputRef.current?.focus();
		}, 100);
		return () => clearTimeout(focusTimer);
	}, []);

	const handleSubmit = (values: FormSchema) => {
		console.log(values);
		// Remove dial code from phone number before submitting
		const getCountry = Object.values(COUNTRIES).find(
			(country) => country.id === String(values.countryCode),
		);

		// Check if country was found
		if (!getCountry) {
			form.setError("countryCode", {
				type: "manual",
				message: "Invalid country selected. Please select a valid country.",
			});
			return;
		}
		const dialCode = getCountry.secondary;

		console.log({
			dialCode,
			phone: values.phone,
		});

		const localPhone = dialCode
			? removeDialCode(values.phone, dialCode)
			: values.phone;
		onSubmit(localPhone, values.countryCode);
	};

	form.watch(["countryCode", "phone"]);
	return (
		<PageTransition>
			<div className="flex flex-col items-center justify-between px-6 py-8 bg-white text-textDark h-[100svh] ">
				{/* Back icon */}
				<div className="flex flex-col items-center justify-center w-full h-full">
					{/* Progress bar */}
					<div className="relative flex justify-center w-full pt-4">
						<Progress value={progress} className="max-w-16" />
					</div>

					{/* Title and description */}
					<div className="flex flex-col items-center justify-between w-full h-full mt-4">
						<h1 className="mb-2 text-4xl font-bold">Verify Phone</h1>
						<Form {...form}>
							<form
								className="space-y-4 flex flex-col justify-center"
								onSubmit={form.handleSubmit(handleSubmit, (error) =>
									console.log(error),
								)}
							>
								<FormField
									control={form.control}
									name="phone"
									render={({ field }) => (
										<FormControl>
											<PhoneInputField
												placeholder="000"
												ref={phoneInputRef}
												value={field.value}
												onChange={(phone) => {
													console.log(phone);
													field.onChange(phone);
												}}
												onBlur={field.onBlur}
												onCountryChange={(countryCode) =>
													form.setValue("countryCode", countryCode)
												}
												onError={(message) =>
													form.setError("phone", { type: "manual", message })
												}
											/>
										</FormControl>
									)}
								/>
								<FormField
									control={form.control}
									name="countryCode"
									render={({ field }) => (
										<FormControl>
											<input
												type="hidden"
												value={field.value}
												onChange={field.onChange}
												onBlur={field.onBlur}
											/>
										</FormControl>
									)}
								/>
								{/* Single error message area - phone errors take priority */}

								{form.formState.errors.phone ? (
									<p className="text-[0.8rem] font-medium text-utility-red">
										{form.formState.errors.phone.message}
									</p>
								) : form.formState.errors.countryCode ? (
									<p className="text-[0.8rem] font-medium text-utility-red">
										{form.formState.errors.countryCode.message}
									</p>
								) : null}
								<div>
									<p className="text-xs flex items-center gap-2 text-textLight -mb-4 text-center">
										<CheckCircle className="size-4" /> {PHONE_NUMBER_STATEMENT}
									</p>
									<SubmitButton
										testId="send-code-button"
										mutationStatus={submitMutationStatus}
										className="w-full mt-6 text-base font-bold text-white bg-primary-600 rounded-xl"
									>
										Send Code
									</SubmitButton>
								</div>
							</form>
						</Form>
						<a
							href={getHelpCenterUrl()}
							target="_blank"
							rel="noopener noreferrer"
							className="text-sm text-center cursor-pointer"
						>
							<span className="text-textDark">Having issues?</span>{" "}
							<span className="text-textDark font-semibold">
								Contact Support
							</span>
						</a>
						<div></div>
						<div></div>
					</div>
				</div>
			</div>
		</PageTransition>
	);
};

export default VerifyPhone;
